import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { message } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import { flushSync } from 'react-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

import { Form } from '@components/Form';
import {
  JOBS_BOARD_POSTS_PRIVATE_QUERY_KEY,
  JOBS_BOARD_POSTS_PUBLIC_QUERY_KEY,
} from '@hooks/jobsBoard/utils';
import { useFileUpload } from '@hooks/shared/useFileUpload/useFileUpload';
import { useGetPublicApplication } from '@hooks/shared/useGetPublicApplication';
import {
  useModalVisibility,
  UseModalVisibilityHookReturn,
} from '@hooks/shared/useModalVisibility';
import { PublicApplicationAPI } from '@lib/api/endpoints/Applications';
import { UsersCommandApi, UsersQueryApi } from '@lib/api/endpoints/users';
import { setUserAppearance } from '@lib/redux.lib/slices/appSlice';
import { RootState, useAppDispatch } from '@lib/redux.lib/store';
import {
  GENDER_OTHER,
  getWardrobeOptions,
  getWardrobeOptionsUK,
  PROFILE_GENDER_TO_WARDROBE_GENDER,
  WARDROBE_GENDERS,
  WARDROBE_LABELS_BY_GENDER,
  WARDROBE_LABELS_BY_GENDER_UK,
  WardrobeGenderType,
  WardrobeLabel,
  WardrobeLabelUK,
  WardrobeMapping,
  WardrobeMappingUK,
} from '@lib/utils/constants';
import { logIfDevelopment } from '@lib/utils/logging';

export const useApplyToApplicationJob = (
  applicationUuid: string,
  application_status: JobsBoardApplicationStatus,
  applicationSubmitModalVisibilityInput?: UseModalVisibilityHookReturn
) => {
  const queryClient = useQueryClient();

  const localApplicationSubmitModalVisibility = useModalVisibility();
  const applicationSubmitModalVisibility =
    applicationSubmitModalVisibilityInput ||
    localApplicationSubmitModalVisibility;

  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );

  const isLoggedIn = useMemo(() => !!loggedInUserId, [loggedInUserId]);

  const { publicApplicationOptions, data: publicApplicationData } =
    useGetPublicApplication(applicationUuid, {
      enabled: !!applicationUuid && isLoggedIn && application_status === 'open',
    });

  const router = useRouter();

  const refreshDataAfterApplying =
    useRefreshDataAfterApplyingToJob(applicationUuid);

  const onApplyButtonClickFn = useCallback(async () => {
    if (!(applicationUuid && application_status === 'open')) {
      message.error('Something went wrong');
      return;
    }

    if (!isLoggedIn) {
      await router.push({
        pathname: '/auth/login',
        query: {
          redirectTo: `/apps/${applicationUuid}`,
        },
      });
      return;
    }

    let applicationData = queryClient.getQueryData<
      PublicApplication | undefined
    >(publicApplicationOptions.queryKey);

    if (!applicationData) {
      applicationData = await queryClient.fetchQuery(publicApplicationOptions);
    }

    if (!applicationData) {
      message.error('Something went wrong when applying to the job');
      return;
    }

    const shouldShowApplyModal =
      !!applicationData?.required_wardrobe?.length ||
      !!applicationData?.questions?.length;

    if (!shouldShowApplyModal) {
      await PublicApplicationAPI.SubmitApplication({
        application_uuid: applicationUuid,
        user_id: loggedInUserId,
        application_responses: [],
      });
      await refreshDataAfterApplying();
      message.success('Application submitted successfully');
      return;
    }
    applicationSubmitModalVisibility.showModal();
  }, [
    applicationSubmitModalVisibility,
    applicationUuid,
    application_status,
    isLoggedIn,
    queryClient,
    refreshDataAfterApplying,
    router,
    publicApplicationOptions,
    loggedInUserId,
  ]);

  const { mutateAsync: onApplyButtonClick, isLoading: isApplying } =
    useMutation({
      mutationFn: onApplyButtonClickFn,
      onError: (error) => {
        logIfDevelopment(error);
        message.error('Something went wrong when applying to the job');
      },
    });

  return {
    onApplyButtonClick,
    isApplying,
    applicationSubmitModalVisibility,
    publicApplicationData,
  };
};

const useRefreshDataAfterApplyingToJob = (applicationUuid: string) => {
  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );

  const queryClient = useQueryClient();

  const refetchActiveJobsBoardPostsQuery = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: JOBS_BOARD_POSTS_PRIVATE_QUERY_KEY,
      exact: false,
      active: true,
    });
  }, [queryClient]);

  const refetchPublicApplicationQuery = useCallback(async () => {
    queryClient.removeQueries(['jobsBoardPostPublic', applicationUuid]);
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['publicApplication', applicationUuid],
      }),
      queryClient.invalidateQueries({
        queryKey: ['jobsBoardPostPrivate', applicationUuid, loggedInUserId],
      }),
    ]);
  }, [queryClient, applicationUuid, loggedInUserId]);

  const appDispatch = useAppDispatch();

  const refreshDataAfterApplying = useCallback(
    async (isUpdatingWardrobe = false) => {
      // Remove inactive queries from cache
      queryClient.removeQueries(JOBS_BOARD_POSTS_PUBLIC_QUERY_KEY, {
        exact: false,
      });
      queryClient.removeQueries(JOBS_BOARD_POSTS_PRIVATE_QUERY_KEY, {
        exact: false,
        active: false,
      });

      const [newUserAppearance] = await Promise.all([
        ...(isUpdatingWardrobe
          ? [UsersQueryApi.getUserAppearance(loggedInUserId)]
          : [undefined]),
        refetchPublicApplicationQuery(),
        refetchActiveJobsBoardPostsQuery(),
      ]);

      if (newUserAppearance) {
        appDispatch(setUserAppearance(newUserAppearance));
        queryClient.setQueryData(
          ['userAppearance', loggedInUserId],
          newUserAppearance
        );
      }
    },
    [
      queryClient,
      appDispatch,
      loggedInUserId,
      refetchActiveJobsBoardPostsQuery,
      refetchPublicApplicationQuery,
    ]
  );

  return refreshDataAfterApplying;
};

export const useApplicationQuestions = (
  publicApplicationData: PublicApplication,
  applicationSubmitModalVisibility: UseModalVisibilityHookReturn
) => {
  const [formInstance] = Form.useForm();

  const refreshDataAfterApplying = useRefreshDataAfterApplyingToJob(
    publicApplicationData?.application_link
  );

  const gender = useSelector(
    (state: RootState) => state.app?.user?.IUserInfoDAO?.about?.gender
  );

  const projectLocationCountry =
    publicApplicationData?.project_detail?.project_country || 'US';

  const isMinor = useSelector(
    (state: RootState) => state.app?.user?.profile?.is_minor ?? false
  );

  const { mappedWardrobe } = useMemo(
    () =>
      getWardrobeData(
        gender,
        projectLocationCountry,
        publicApplicationData?.required_wardrobe,
        isMinor
      ),
    [
      gender,
      projectLocationCountry,
      publicApplicationData?.required_wardrobe,
      isMinor,
    ]
  );

  const questions = useMemo(
    () =>
      publicApplicationData?.questions
        ?.filter(
          (q) =>
            !!q?.list_column_id &&
            q?.type !== 'attachment' &&
            !!q?.validation_rules?.responseType &&
            (
              [
                'Free text',
                'Multiple-choice',
                'Single-choice',
              ] as CustomQuestionResponseType[]
            ).includes(q?.validation_rules?.responseType)
        )
        ?.map((question) => {
          const questionObj = {
            required: !!question?.validation_rules?.isRequired,
            responseType: question?.validation_rules?.responseType,
            label: question?.validation_rules?.prompt,
            listColumnId: question?.list_column_id,
            descriptionImage: question?.validation_rules?.description_image,
            options: question?.validation_rules?.options,
          };
          return questionObj as ParsedQuestion;
        }) || [],
    [publicApplicationData?.questions]
  );

  const attachmentQuestions = useMemo(
    () =>
      publicApplicationData?.questions
        ?.filter((q) => !!q?.list_column_id && q?.type === 'attachment')
        ?.map((question) => {
          const questionObj = {
            required: !!question?.validation_rules?.isRequired,
            label: question?.validation_rules?.prompt,
            listColumnId: question?.list_column_id,
            descriptionImage: question?.validation_rules?.description_image,
            uploadMin: question?.validation_rules?.minimumUploads,
            uploadMax: question?.validation_rules?.maximumUploads,
          };
          return questionObj as ParsedAttachmentQuestion;
        }) || [],
    [publicApplicationData?.questions]
  );

  const [columnIdToAttachmentUrls, setColumnIdToAttachmentUrls] = useState<{
    [key: number]: string[];
  }>({});

  const getAttachmentQuestionErrorMessage = useCallback(
    (attachmentQuestion: ParsedAttachmentQuestion) => {
      const attachmentUrls =
        columnIdToAttachmentUrls?.[attachmentQuestion?.listColumnId] || [];
      if (
        !attachmentQuestion?.required &&
        ![attachmentQuestion?.uploadMax, attachmentQuestion?.uploadMin].some(
          (num) => Number.isFinite(num) && num > 0
        )
      ) {
        return null;
      }
      if (attachmentQuestion?.required) {
        if (!attachmentUrls?.length) {
          return 'This field is required';
        }

        if (
          Number.isFinite(attachmentQuestion?.uploadMin) &&
          attachmentQuestion?.uploadMin > 0 &&
          attachmentUrls?.length < attachmentQuestion?.uploadMin
        ) {
          return `Please upload at least ${attachmentQuestion?.uploadMin} file(s)`;
        }

        if (
          Number.isFinite(attachmentQuestion?.uploadMax) &&
          attachmentQuestion?.uploadMax > 0 &&
          attachmentUrls?.length > attachmentQuestion?.uploadMax
        ) {
          return `Please upload at most ${attachmentQuestion?.uploadMax} file(s)`;
        }
      } else if (attachmentUrls?.length) {
        if (
          Number.isFinite(attachmentQuestion?.uploadMin) &&
          attachmentQuestion?.uploadMin > 0 &&
          attachmentUrls?.length < attachmentQuestion?.uploadMin
        ) {
          return `Please upload at least ${attachmentQuestion?.uploadMin} file(s)`;
        }

        if (
          Number.isFinite(attachmentQuestion?.uploadMax) &&
          attachmentQuestion?.uploadMax > 0 &&
          attachmentUrls?.length > attachmentQuestion?.uploadMax
        ) {
          return `Please upload at most ${attachmentQuestion?.uploadMax} file(s)`;
        }
      }
      return null;
    },
    [columnIdToAttachmentUrls]
  );

  const areAttachmentsValid = useMemo(() => {
    if (!attachmentQuestions?.length) return true;
    for (const question of attachmentQuestions) {
      const errorMessage = getAttachmentQuestionErrorMessage(question);
      if (errorMessage) {
        return false;
      }
    }
    return true;
  }, [attachmentQuestions, getAttachmentQuestionErrorMessage]);

  const [columnIdToFileIdsLoading, setColumnIdToFileIdsLoading] = useState<{
    [key: number]: string[];
  }>({});
  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );

  const areAnyFilesUploading = useMemo(() => {
    return Object.values(columnIdToFileIdsLoading)?.some(
      (fileIds) => !!fileIds?.length
    );
  }, [columnIdToFileIdsLoading]);

  const onSubmitFormFn = useCallback(async () => {
    // attachmentQuestions are valid, but we need to check attachments

    if (
      !areAttachmentsValid ||
      areAnyFilesUploading ||
      !publicApplicationData?.application_link ||
      !formInstance
    ) {
      return;
    }

    // attachments are valid
    const questionValues = formInstance?.getFieldsValue?.();

    let wardrobePayload: CreateOrUpdateUserAppearanceCall | undefined;

    if (mappedWardrobe?.length > 0) {
      wardrobePayload = mappedWardrobe?.reduce((acc, wardrobeField) => {
        const responseValue = questionValues?.[wardrobeField?.profileField];
        if (!responseValue) {
          return acc;
        }
        acc[wardrobeField?.profileField] = responseValue;
        return acc;
      }, {} as CreateOrUpdateUserAppearanceCall);
    }

    const questionsPayload =
      questions?.reduce((acc, question) => {
        const responseValue = questionValues?.[question?.listColumnId];
        const isValidResponse =
          typeof responseValue === 'string' ||
          (Array.isArray(responseValue) &&
            responseValue.every((item) => typeof item === 'string'));
        if (!isValidResponse) {
          return acc;
        }

        const obj = {
          list_column_id: question?.listColumnId,
          response: {
            value: responseValue,
          },
        };
        acc.push(obj);
        return acc;
      }, [] as ApplicationResponse[]) || [];

    const attachmentsPayload =
      attachmentQuestions?.reduce((acc, question) => {
        const responseValue =
          columnIdToAttachmentUrls?.[question?.listColumnId];
        const isValidResponse =
          Array.isArray(responseValue) &&
          responseValue.every((item) => typeof item === 'string');
        if (!isValidResponse) {
          return acc;
        }

        const obj = {
          list_column_id: question?.listColumnId,
          response: {
            value: responseValue,
          },
        };
        acc.push(obj);
        return acc;
      }, [] as ApplicationResponse[]) || [];

    const applicationResponses = [...questionsPayload, ...attachmentsPayload];

    const isUpdatingWardrobe = !!Object.keys(wardrobePayload || {})?.length;

    await Promise.all([
      PublicApplicationAPI.SubmitApplication({
        application_uuid: publicApplicationData?.application_link,
        user_id: loggedInUserId,
        application_responses: applicationResponses,
      }),
      ...(isUpdatingWardrobe
        ? [
            UsersCommandApi.updateUserAppearance(
              loggedInUserId,
              wardrobePayload
            ),
          ]
        : []),
    ]);

    await refreshDataAfterApplying(isUpdatingWardrobe);
    message.success('Application submitted successfully');
    flushSync(() => applicationSubmitModalVisibility.hideModal());
    return;
  }, [
    areAttachmentsValid,
    areAnyFilesUploading,
    formInstance,
    questions,
    publicApplicationData,
    loggedInUserId,
    columnIdToAttachmentUrls,
    attachmentQuestions,
    applicationSubmitModalVisibility,

    mappedWardrobe,
    refreshDataAfterApplying,
  ]);

  const { mutateAsync: onSubmitForm, isLoading: isSubmittingForm } =
    useMutation({
      mutationFn: onSubmitFormFn,
      onError: () => {
        message.error('Something went wrong when submitting the application');
      },
    });

  const [wasSubmitButtonClicked, setWasSubmitButtonClicked] = useState(false);

  const onSubmitButtonClick = useCallback(() => {
    setWasSubmitButtonClicked(true);
    formInstance.submit();
  }, [formInstance]);

  const onFileRemove = useCallback((fileUrl: string, columnId: number) => {
    if (!(fileUrl && columnId)) return;
    setColumnIdToAttachmentUrls((prev) => {
      const finalResult = { ...prev };
      const newList = prev[columnId]?.filter((url) => url !== fileUrl) || [];
      if (!newList?.length) {
        delete finalResult[columnId];
        return finalResult;
      }
      return {
        ...finalResult,
        [columnId]: newList,
      };
    });
  }, []);

  const { uploadFile, validateFile, fileErrorMessageRef } = useFileUpload();

  const handleFileUpload = useCallback(
    async (input: UploadChangeParam<UploadFile>, listColumnId: number) => {
      const setListColumnIdWithFileIdAsNotLoading = () => {
        setColumnIdToFileIdsLoading((prev) => {
          const finalResult = { ...prev };
          const newList =
            prev[listColumnId]?.filter((id) => id !== input?.file?.uid) || [];
          if (!newList?.length) {
            delete finalResult[listColumnId];
            return finalResult;
          }
          return {
            ...finalResult,
            [listColumnId]: Array.from(new Set(newList)),
          };
        });
      };
      const onUploadFail = () => {
        setListColumnIdWithFileIdAsNotLoading();
        message.error('Something went wrong when uploading the file');
      };

      try {
        if (!input?.file) {
          message.error('Please select a file');
          return;
        }
        setColumnIdToFileIdsLoading((prev) => {
          return {
            ...prev,
            [listColumnId]: Array.from(
              new Set([...(prev[listColumnId] || []), input?.file?.uid])
            ),
          };
        });
        const isValid = await validateFile(input?.file as unknown as File, {
          fileExtensions: ['png', 'jpeg', 'jpg'],
        });
        if (!isValid) {
          setListColumnIdWithFileIdAsNotLoading();
          message.error(fileErrorMessageRef?.current || 'Something went wrong');
          return;
        }

        await uploadFile(
          input?.file as unknown as File,
          (uploadUrl) => {
            if (!uploadUrl?.fields?.key) {
              onUploadFail();
              return;
            }
            const imgSrc = `${process.env.NEXT_PUBLIC_CF_PHOTOS_BASE_URL}/${uploadUrl.fields.key}`;
            setColumnIdToAttachmentUrls((prev) => ({
              ...prev,
              [listColumnId]: [...(prev[listColumnId] || []), imgSrc],
            }));
            setListColumnIdWithFileIdAsNotLoading();
          },
          undefined,
          onUploadFail
        );
      } catch (error) {
        onUploadFail();
      }
    },
    [validateFile, uploadFile, fileErrorMessageRef]
  );

  return {
    questions,
    attachmentQuestions,
    formInstance,
    onSubmitForm,
    columnIdToAttachmentUrls,
    setColumnIdToAttachmentUrls,
    handleFileUpload,
    columnIdToFileIdsLoading,
    onFileRemove,
    wasSubmitButtonClicked,
    getAttachmentQuestionErrorMessage,
    onSubmitButtonClick,
    areAnyFilesUploading,
    isSubmittingForm,
    mappedWardrobe,
  };
};

export type ApplicationQuestionsData = ReturnType<
  typeof useApplicationQuestions
>;

type ParsedQuestion = {
  required: boolean;
  responseType: CustomQuestionResponseType;
  label: string;
  listColumnId: number;
  descriptionImage: string;
  options: string[];
};

export type ParsedAttachmentQuestion = {
  required: boolean;
  label: string;
  listColumnId: number;
  descriptionImage: string;
  uploadMin: number;
  uploadMax: number;
};

const getWardrobeData = (
  gender: string | undefined,
  projectLocationCountry: ProductionCountry,
  requiredWardrobe: string[] | undefined,
  isMinor: boolean | undefined
) => {
  const baseWardrobeGender = (PROFILE_GENDER_TO_WARDROBE_GENDER[gender] ||
    gender ||
    GENDER_OTHER) as string;

  const wardrobeGender = (
    (WARDROBE_GENDERS as unknown as string[]).includes(baseWardrobeGender)
      ? baseWardrobeGender
      : GENDER_OTHER
  ) as WardrobeGenderType;

  const wardrobeLabelsByGender =
    projectLocationCountry === 'UK'
      ? WARDROBE_LABELS_BY_GENDER_UK
      : WARDROBE_LABELS_BY_GENDER;

  const wardrobeLabelMapper =
    projectLocationCountry === 'UK' ? WardrobeLabelUK : WardrobeLabel;

  const pickedWardobeMapping =
    projectLocationCountry === 'UK' ? WardrobeMappingUK : WardrobeMapping;

  const getOptionsFn =
    projectLocationCountry === 'UK' ? getWardrobeOptionsUK : getWardrobeOptions;

  const attributeSet = wardrobeLabelsByGender[wardrobeGender] || [];
  const mappedWardrobe =
    attributeSet?.reduce((acc, attribute) => {
      const profileField = pickedWardobeMapping?.[attribute] as
        | string
        | undefined;
      const label = wardrobeLabelMapper?.[profileField] as string | undefined;
      if (
        requiredWardrobe?.includes(attribute) &&
        attribute &&
        profileField &&
        label
      ) {
        const options = getOptionsFn(profileField, isMinor) as
          | string[]
          | undefined;

        if (options) {
          acc.push({
            profileField,
            options,
            label,
          });
        } else {
          acc.push({
            profileField,
            label,
          });
        }
      }
      return acc;
    }, [] as MappedWardobeField[]) || [];

  return { mappedWardrobe };
};

export type MappedWardobeField = {
  profileField: string;
  options?: string[];
  label: string;
};
